import React, { useContext, useEffect, useState } from "react";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import { DefaultButton, Icon, PrimaryButton, useTheme } from "@fluentui/react";
import { t } from "i18next";
import { AuthRoutes, resolveBackendForHost } from "../../constants/Constants";
import userContext from "../../context/users/UserContext";

// A deploy replaces the hashed files under /assets, so a tab still running the
// previous index.html asks for a chunk that no longer exists (e.g. the mobile
// MobileStudyListContainer-<hash>.js). Each browser words that failure differently.
const CHUNK_LOAD_ERROR =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|Loading (CSS )?chunk .* failed|is not a valid JavaScript MIME type/i;

// Remembers the last automatic reload so a chunk that is genuinely missing on the
// server shows this page instead of reloading forever.
const RELOAD_KEY = "chunkErrorReloadAt";
const RELOAD_WINDOW_MS = 30_000;

export const isChunkLoadError = (error) =>
  error?.name === "ChunkLoadError" || CHUNK_LOAD_ERROR.test(error?.message || "");

const reloadedRecently = () => {
  try {
    return Date.now() - Number(sessionStorage.getItem(RELOAD_KEY) || 0) < RELOAD_WINDOW_MS;
  } catch (e) {
    return true;
  }
};

const markReload = () => {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch (e) {
    /* ignore */
  }
};

const RouteErrorPage = () => {
  const error = useRouteError();
  const theme = useTheme();
  const { customerSupportNumber } = useContext(userContext);
  const [showDetails, setShowDetails] = useState(false);

  const notFound = isRouteErrorResponse(error) && error.status === 404;
  // A stale chunk usually clears with one fresh load, so try that before showing
  // anything. If it fails again the cause is something else (network, blocked or
  // missing file), so the user gets the generic message below, not "updated".
  // Decided once per error: re-checking on every render would reload the page when
  // the user clicks anything here after the 30s window has passed.
  const [autoReloading] = useState(() => !notFound && isChunkLoadError(error) && !reloadedRecently());

  // Same gating as the navbar: white-labelled hosts don't show the support number.
  const hideBranding = resolveBackendForHost(window.location.host).matched === true;
  const supportPhone = !hideBranding && customerSupportNumber ? customerSupportNumber : null;

  useEffect(() => {
    console.error(error);
    if (autoReloading) {
      markReload();
      window.location.reload();
    }
  }, [error, autoReloading]);

  // Full page loads (not router navigations) so a stale tab picks up the new build.
  const reload = () => {
    markReload();
    window.location.reload();
  };
  const goHome = () => window.location.assign(AuthRoutes.STUDYLIST);

  if (autoReloading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: theme.semanticColors.bodySubtext }}>
        {t("label.route-error-reloading", "Reloading…")}
      </div>
    );
  }

  const title = notFound
    ? t("label.route-error-not-found-title", "Page not found")
    : t("label.route-error-generic-title", "Something went wrong");
  const message = notFound
    ? t("label.route-error-not-found-message", "The page you are looking for does not exist or may have been moved.")
    : t("label.route-error-generic-message", "We couldn't load this page.");

  const details = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error?.message || String(error);

  const linkStyle = { color: theme.palette.themePrimary };

  return (
    <div
      role="alert"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: 16,
        height: "100%",
        minHeight: 320,
        padding: "32px 16px",
        boxSizing: "border-box",
        color: theme.semanticColors.bodyText,
      }}
    >
      <Icon
        iconName={notFound ? "SearchIssue" : "Warning"}
        style={{ fontSize: 48, color: theme.palette.orangeLighter }}
      />
      <div style={{ fontSize: 22, fontWeight: 600 }}>{title}</div>
      <div style={{ fontSize: 15, maxWidth: 420, lineHeight: 1.5, color: theme.semanticColors.bodySubtext }}>
        {message}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 12, marginTop: 8 }}>
        {!notFound && <PrimaryButton text={t("label.route-error-reload", "Reload page")} onClick={reload} />}
        <DefaultButton text={t("label.route-error-home", "Go to Study List")} onClick={goHome} />
      </div>

      {!notFound && (
        <div style={{ fontSize: 13, maxWidth: 420, lineHeight: 1.6, color: theme.semanticColors.bodySubtext }}>
          {t("label.route-error-contact-support", "If the problem continues, contact support.")}
          {supportPhone && (
            <div>
              <a href={`tel:${supportPhone}`} style={linkStyle}>{supportPhone}</a>
            </div>
          )}
        </div>
      )}

      {!notFound && (
        <div style={{ maxWidth: 520, width: "100%" }}>
          <button
            type="button"
            onClick={() => setShowDetails((s) => !s)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 12,
              color: theme.semanticColors.bodySubtext,
              textDecoration: "underline",
            }}
          >
            {showDetails
              ? t("label.route-error-hide-details", "Hide technical details")
              : t("label.route-error-show-details", "Show technical details")}
          </button>
          {showDetails && (
            <pre
              style={{
                marginTop: 8,
                padding: 12,
                fontSize: 12,
                textAlign: "left",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                borderRadius: 4,
                background: theme.semanticColors.bodyStandoutBackground,
              }}
            >
              {details}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

export default RouteErrorPage;
